import React, { useState, useEffect } from "react";
import {
  Card,
  Select,
  Tag,
  Button,
  message,
  Space,
  Typography,
  Form,
  Input,
  DatePicker,
  Table,
} from "antd";
import { LeaveTypeEnum, LeaveTypeEnumLabel } from "Constants/Enums/LeavePolicyEnum";
import { useNavigate } from "react-router-dom";
import LeaveRequestService from "Services/EmployeeServices/LeaveRequestService";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import moment from "moment";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const LeaveRequest = () => {
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [myRequests, setMyRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);

  const {
    leavePolicies,
    loading,
    fetchLeaveBalance,
    submitLeaveRequest,
    hasAvailableDays,
    calculateDays,
  } = LeaveRequestService();

  // Fetch employee's leave balance and my requests on component mount
  useEffect(() => {
    fetchLeaveBalance();
    fetchMyRequests();
  }, []);

  // Fetch my leave requests
  const fetchMyRequests = async () => {
    try {
      setRequestsLoading(true);
      const response = await axiosInstance.get(API.leaveRequest.myRequests);
      setMyRequests(response.data);
    } catch (error) {
      console.error("Error fetching my leave requests:", error);
      message.error("Failed to load your leave requests");
    } finally {
      setRequestsLoading(false);
    }
  };

  // Handle policy selection
  const handlePolicySelect = (policyId) => {
    const policy = leavePolicies.find((p) => p.leavePolicyId === policyId);
    setSelectedPolicy(policy);
    // Set the leave type in the form
    form.setFieldsValue({
      leaveType: policy.leaveType,
    });
  };

  // Handle form submission
  const onFinish = async (values) => {
    if (!selectedPolicy) {
      message.warning("Please select a leave policy");
      return;
    }

    if (!hasAvailableDays(selectedPolicy)) {
      message.error("No available days for this policy");
      return;
    }

    try {
      const payload = {
        leaveType: values.leaveType,
        fromDate: values.dateRange[0].format("YYYY-MM-DD"),
        toDate: values.dateRange[1].format("YYYY-MM-DD"),
        reason: values.reason || "",
      };

      await submitLeaveRequest(payload);
      form.resetFields();
      setSelectedPolicy(null);
      // Refresh leave balance and requests to update data
      await fetchLeaveBalance();
      await fetchMyRequests();
    } catch (error) {
      console.error("Error in form submission:", error);
    }
  };

  // Disable past dates
  const disabledDate = (current) => {
    return current && current < moment().startOf("day"); // using moment
  };

  // Handle date range change
  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const startDate = dates[0].format("YYYY-MM-DD");
      const endDate = dates[1].format("YYYY-MM-DD");
      const days = calculateDays(startDate, endDate);
      form.setFieldsValue({ days: days });
    }
  };

  

  

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* Apply for Leave Card - First Row */}
      <Card
        title="Apply for  Leave"
        loading={loading}
        className="card-professional"
        style={{ marginBottom: "24px" }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
          {/* Available Policies Section */}
          <div>
            <Text strong style={{ display: "block", marginBottom: 16, fontSize: "16px" }}>
              Available Leave Policies
            </Text>

            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              {leavePolicies.map((policy) => (
                <Card
                  key={policy.leavePolicyId}
                  size="small"
                  style={{
                    border:
                      selectedPolicy?.leavePolicyId === policy.leavePolicyId
                        ? "2px solid #1890ff"
                        : "1px solid #d9d9d9",
                    cursor: hasAvailableDays(policy) ? "pointer" : "not-allowed",
                    opacity: hasAvailableDays(policy) ? 1 : 0.6,
                    background:
                      selectedPolicy?.leavePolicyId === policy.leavePolicyId ? "#f0f8ff" : "white",
                  }}
                  onClick={() =>
                    hasAvailableDays(policy) && handlePolicySelect(policy.leavePolicyId)
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ display: "block", marginBottom: 4 }}>
                        {policy.policyName}
                      </Text>
                      <Tag style={{ margin: 0, marginBottom: 8, border: "1px solid #d9d9d9" }}>
                        {LeaveTypeEnumLabel[policy.leaveType] || "Unknown"}
                      </Tag>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Policy Year: {policy.policyYear}
                      </div>
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          color: hasAvailableDays(policy) ? "#389e0d" : "#cf1322",
                        }}
                      >
                        {policy.availableDays} / {policy.grantedDays}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>days available</div>
                      {policy.usedDays > 0 && (
                        <div style={{ fontSize: "11px", color: "#999" }}>
                          {policy.usedDays} days used
                        </div>
                      )}
                    </div>
                  </div>

                  {!hasAvailableDays(policy) && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "4px 8px",
                        background: "#f5f5f5",
                        borderRadius: "4px",
                        fontSize: "12px",
                        textAlign: "center",
                        color: "#999",
                      }}
                    >
                      No available days
                    </div>
                  )}
                </Card>
              ))}
            </Space>

            {/* Summary */}
            {leavePolicies.length > 0 && (
              <Card size="small" style={{ marginTop: 16, background: "#fafafa" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: "12px", color: "#666" }}>Total Policies</div>
                    <div style={{ fontSize: "16px", fontWeight: "bold" }}>
                      {leavePolicies.length}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "12px", color: "#666" }}>Available Days</div>
                    <div style={{ fontSize: "16px", fontWeight: "bold", color: "#389e0d" }}>
                      {leavePolicies.reduce((sum, policy) => sum + policy.availableDays, 0)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "12px", color: "#666" }}>Used Days</div>
                    <div style={{ fontSize: "16px", fontWeight: "bold", color: "#fa8c16" }}>
                      {leavePolicies.reduce((sum, policy) => sum + policy.usedDays, 0)}
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Application Form */}
          <div>
            <Text strong style={{ display: "block", marginBottom: 16, fontSize: "16px" }}>
              Leave Application Form
            </Text>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              disabled={!selectedPolicy || !hasAvailableDays(selectedPolicy)}
            >
              {/* Selected Policy Info */}
              {selectedPolicy && (
                <Card
                  size="small"
                  style={{ marginBottom: 16, background: "#f0f8ff", border: "1px solid #91d5ff" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <Text strong>{selectedPolicy.policyName}</Text>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        {LeaveTypeEnumLabel[selectedPolicy.leaveType]} •{" "}
                        {selectedPolicy.availableDays} days available
                      </div>
                    </div>
                    <Button
                      size="small"
                      type="text"
                      onClick={() => {
                        setSelectedPolicy(null);
                        form.resetFields();
                      }}
                    >
                      Change Policy
                    </Button>
                  </div>
                </Card>
              )}

              {/* Leave Type (hidden but included in form) */}
              <Form.Item name="leaveType" hidden>
                <Input />
              </Form.Item>

              {/* Date Range */}
              <Form.Item
                name="dateRange"
                label="Leave Dates"
                rules={[{ required: true, message: "Please select leave dates" }]}
              >
                <RangePicker
                  style={{ width: "100%" }}
                  disabledDate={disabledDate}
                  onChange={handleDateChange}
                  format="YYYY-MM-DD"
                  allowEmpty={[true, true]}
                />
              </Form.Item>

              {/* Calculated Days */}
              <Form.Item name="days" label="Number of Days" initialValue={0}>
                <Input disabled placeholder="Will be calculated based on selected dates" />
              </Form.Item>

              {/* Reason */}
              <Form.Item
                name="reason"
                label="Reason for Leave"
                rules={[{ required: true, message: "Please provide a reason for leave" }]}
              >
                <TextArea
                  rows={4}
                  placeholder="Please provide the reason for your leave request..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              {/* Submit Button */}
              <Form.Item>
                <Button
                  className="btn-primary"
                  htmlType="submit"
                  style={{ width: "100%" }}
                  disabled={!selectedPolicy || !hasAvailableDays(selectedPolicy)}
                  loading={loading}
                >
                  Submit Leave Request
                </Button>
              </Form.Item>
            </Form>

            {!selectedPolicy && (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  background: "#f9f9f9",
                  borderRadius: "6px",
                  border: "1px dashed #d9d9d9",
                }}
              >
                <Text type="secondary">
                  Please select a leave policy from the left to apply for leave
                </Text>
              </div>
            )}

            {selectedPolicy && !hasAvailableDays(selectedPolicy) && (
              <div
                style={{
                  textAlign: "center",
                  padding: "16px",
                  background: "#fff2f0",
                  borderRadius: "6px",
                  border: "1px solid #ffccc7",
                }}
              >
                <Text type="danger">
                  No available days left for {selectedPolicy.policyName}. Please select another
                  policy.
                </Text>
              </div>
            )}
          </div>
        </div>
      </Card>
    </DashboardLayout>
  );
};

export default LeaveRequest;
